import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartBar, faDownload, 
  faRefresh, faExclamationTriangle,
  faCheckCircle, faClock, faUsers, faClipboardList, faTrash,
  faBuilding, faPercentage, faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import StatWidget from '../components/dashboard/widgets/StatWidget';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import SubmissionTrendsChart from '../components/analytics/SubmissionTrendsChart';
import TemplatePerformanceTable from '../components/analytics/TemplatePerformanceTable';
import {
  getAnalyticsOverview,
  getStatusDistribution,
  exportAnalyticsReport,
  type AnalyticsOverview,
  type StatusDistribution
} from '../services/analyticsApi';

const Analytics: React.FC = () => {
  // **STATE MANAGEMENT**
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // **FILTERS**
  const [selectedPeriod, setSelectedPeriod] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // **DATA STATE**
  const [analyticsData, setAnalyticsData] = useState<AnalyticsOverview | null>(null);
  const [statusData, setStatusData] = useState<StatusDistribution | null>(null);
  
  // **EXPORT STATE**
  const [exporting, setExporting] = useState(false);

  // **DATA LOADING**
  const loadAnalyticsData = useCallback(async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const [overviewResponse, statusResponse] = await Promise.all([
        getAnalyticsOverview({ year: selectedYear, period: selectedPeriod }),
        getStatusDistribution(selectedYear)
      ]);

      setAnalyticsData(overviewResponse.data);
      setStatusData(statusResponse.data);

    } catch (err: any) {
      console.error('Error loading analytics data:', err);
      setError(err.message || 'Fehler beim Laden der Analytics-Daten');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedYear, selectedPeriod]);

  // **INITIAL LOAD**
  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  // **EVENT HANDLERS**
  const handleRefresh = () => {
    loadAnalyticsData(true);
  };

  const handlePeriodChange = (period: '7d' | '30d' | '90d' | '1y') => {
    setSelectedPeriod(period);
  };

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
  };

  const handleExport = async (format: 'xlsx' | 'csv' | 'pdf') => {
    try {
      setExporting(true);
      const response = await exportAnalyticsReport(format, selectedYear);
      
      // **TODO: Handle download URL**
      console.log('Export response:', response.data);
      
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  // **YEAR OPTIONS**
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 5; i--) {
      years.push(i);
    }
    return years;
  };

  if (loading) {
    return <LoadingSpinner size="medium" text="Lade Analytics..." />;
  }

  if (error) {
    return (
      <div className="p-6 max-w-full mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 text-3xl mb-4" />
          <p className="text-red-700 font-bold text-lg">Fehler beim Laden der Analytics</p>
          <p className="text-red-600 text-sm mt-2">{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-4 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* **KOMPAKTE TOOLBAR - KONSISTENT MIT ANDEREN SEITEN** */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          {/* **TITLE** */}
          <div className="flex items-center">
          </div>
          
          {/* **CONTROLS** */}
          <div className="flex items-center space-x-3">
            {/* **YEAR SELECTOR** */}
            <select
              value={selectedYear}
              onChange={(e) => handleYearChange(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent bg-white text-sm"
            >
              {getYearOptions().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>

            {/* **PERIOD SELECTOR** */}
            <div className="flex bg-gray-100 rounded-md p-1">
              {(['7d', '30d', '90d', '1y'] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => handlePeriodChange(period)}
                  className={`px-3 py-1 text-sm rounded transition-colors ${
                    selectedPeriod === period
                      ? 'bg-purple-500 text-white font-medium'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {period === '7d' ? '7T' : 
                   period === '30d' ? '30T' : 
                   period === '90d' ? '90T' : '1J'}
                </button>
              ))}
            </div>

            {/* **REFRESH BUTTON** */}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors disabled:opacity-50 text-sm"
              title="Aktualisieren"
            >
              <FontAwesomeIcon 
                icon={faRefresh} 
                className={refreshing ? 'animate-spin' : ''} 
              />
            </button>

            {/* **EXPORT DROPDOWN** */}
            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleExport(e.target.value as 'xlsx' | 'csv' | 'pdf');
                  e.target.value = '';
                }
              }}
              className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm"
              disabled={exporting}
            >
              <option value="">Export</option>
              <option value="xlsx">Excel</option>
              <option value="csv">CSV</option>
              <option value="pdf">PDF</option>
            </select>
          </div>
        </div>
      </div>

      {/* **CONTENT BEREICH** */}
      <div className="p-4 space-y-6">
        {analyticsData && (
          <>
            {/* **OVERVIEW STATS** */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <StatWidget
                title="Gesamt Submissions"
                value={analyticsData.overview.totalSubmissions}
                icon={faClipboardList}
                color="blue"
                subtitle={`Jahr ${selectedYear}`}
              />
              <StatWidget
                title="Abgeschlossen"
                value={analyticsData.overview.completedSubmissions}
                icon={faCheckCircle}
                color="green"
                subtitle={`${analyticsData.overview.completionRate}% Erfolgsrate`}
              />
              <StatWidget
                title="In Bearbeitung"
                value={analyticsData.overview.pendingSubmissions}
                icon={faClock}
                color="yellow"
                subtitle="Aktuelle Bearbeitungen"
              />
              <StatWidget
                title="Überfällig"
                value={analyticsData.overview.overdueSubmissions}
                icon={faExclamationTriangle}
                color="red"
                subtitle="Benötigen Aufmerksamkeit"
              />
              <StatWidget
                title="Gelöscht"
                value={analyticsData.overview.deletedSubmissions || 0}
                icon={faTrash}
                color="red"
                subtitle="Archivierte Submissions"
              />
            </div>

            {/* **CHARTS** */}
            <SubmissionTrendsChart 
              data={analyticsData.trends} 
              period={selectedPeriod}
            />

            <TemplatePerformanceTable data={analyticsData.templatePerformance} />

            {/* **ALLE LIEFERANTEN PERFORMANCE** */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* **HEADER** */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="bg-emerald-500 p-3 rounded-lg mr-4">
                      <FontAwesomeIcon icon={faUsers} className="text-white text-lg" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Lieferanten Performance</h3>
                      <p className="text-sm text-gray-600 mt-1">Alle Lieferanten und ihre Submission-Statistiken</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">{analyticsData.supplierPerformance.length}</div>
                    <div className="text-sm text-gray-600">Aktive Lieferanten</div>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {analyticsData.supplierPerformance.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {analyticsData.supplierPerformance.map((supplier, index) => (
                      <motion.div
                        key={supplier._id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow"
                      >
                        {/* **SUPPLIER HEADER** */}
                        <div className="flex items-center mb-4">
                          <div className="bg-emerald-100 p-2 rounded-lg mr-3">
                            <FontAwesomeIcon icon={faBuilding} className="text-emerald-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 truncate" title={supplier.supplierName}>
                              {supplier.supplierName}
                            </h4>
                            <p className="text-xs text-gray-600">
                              {supplier.lastActivity 
                                ? `Zuletzt aktiv: ${new Date(supplier.lastActivity).toLocaleDateString('de-DE')}`
                                : 'Keine Aktivität'
                              }
                            </p>
                          </div>
                        </div>

                        {/* **STATISTICS** */}
                        <div className="space-y-3">
                          {/* **SUBMISSIONS** */}
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Submissions</span>
                            <span className="font-bold text-lg text-gray-900">{supplier.totalSubmissions}</span>
                          </div>

                          {/* **COMPLETED** */}
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Abgeschlossen</span>
                            <span className="font-bold text-emerald-600">{supplier.completed}</span>
                          </div>

                          {/* **SUCCESS RATE** */}
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Erfolgsrate</span>
                              <span className="font-bold text-gray-900">{Math.round(supplier.completionRate)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${supplier.completionRate}%` }}
                                transition={{ duration: 1, delay: index * 0.05 }}
                                className={`h-2 rounded-full ${
                                  supplier.completionRate >= 80 
                                    ? 'bg-emerald-500'
                                    : supplier.completionRate >= 60
                                    ? 'bg-yellow-500'
                                    : 'bg-red-500'
                                }`}
                              />
                            </div>
                          </div>

                          {/* **PROGRESS** */}
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Ø Fortschritt</span>
                            <span className={`font-bold ${
                              supplier.avgProgress >= 80 
                                ? 'text-emerald-600'
                                : supplier.avgProgress >= 60
                                ? 'text-yellow-600'
                                : 'text-red-600'
                            }`}>
                              {Math.round(supplier.avgProgress)}%
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FontAwesomeIcon icon={faUsers} className="text-gray-300 text-5xl mb-4" />
                    <p className="text-gray-600 font-medium text-lg">Keine Lieferanten-Daten verfügbar</p>
                    <p className="text-gray-500 text-sm mt-2">Erstellen Sie Submissions für Lieferanten</p>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
};

export default Analytics;